import { ApiClientError, type ApiClient } from "../../../infrastructure/http/apiClient";
import type { PublicBookingContext } from "../domain/bookingContext";

export async function getPublicBookingContext(
  apiClient: ApiClient,
  tenantSlug: string
): Promise<PublicBookingContext> {
  const response = await apiClient.get<unknown>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/booking-context`
  );

  if (!isPublicBookingContext(response)) {
    throw new ApiClientError("The public booking context response did not match the expected contract.", {
      code: "invalid_response"
    });
  }

  return response;
}

function isPublicBookingContext(value: unknown): value is PublicBookingContext {
  if (!isRecord(value) || !isRecord(value.tenant) || !isRecord(value.booking_policy)) {
    return false;
  }

  return typeof value.tenant.name === "string"
    && typeof value.tenant.slug === "string"
    && typeof value.tenant.vertical === "string"
    && typeof value.tenant.timezone === "string"
    && typeof value.booking_policy.timezone === "string"
    && typeof value.booking_policy.language === "string"
    && typeof value.booking_policy.date_format === "string"
    && typeof value.booking_policy.time_format === "string"
    && typeof value.booking_policy.week_starts_on === "number"
    && typeof value.booking_policy.appointment_lead_time_minutes === "number"
    && typeof value.booking_policy.booking_resource_selection_mode === "string"
    && typeof value.booking_policy.booking_presence_confirmation === "string"
    && typeof value.booking_policy.booking_auto_release_unconfirmed === "boolean"
    && typeof value.booking_policy.booking_confirmation_deadline_minutes === "number"
    && typeof value.booking_policy.booking_arrival_checkpoint === "string"
    && typeof value.booking_policy.customer_capture_mode === "string"
    && Array.isArray(value.booking_policy.extended_customer_fields)
    && value.booking_policy.extended_customer_fields.every((field) => typeof field === "string");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
